// 📂 src/pages/CameraDetail.jsx

import { useEffect, useState, useMemo } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import authAxios from "../api/authAxios";

import {
  FiChevronLeft,
  FiAlertTriangle,
  FiCamera,
  FiActivity,
  FiClock,
  FiServer,
} from "react-icons/fi";

import EnterpriseDVRPlayer from "./CameraDetail/EnterpriseDVRPlayer";

// Small status pill
const StatusIndicator = ({ online }) => (
  <div className="flex items-center gap-2">
    {online ? (
      <>
        <div className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
        </div>
        <span className="text-emerald-400 font-medium text-xs md:text-sm">
          Online
        </span>
      </>
    ) : (
      <>
        <div className="w-3 h-3 rounded-full bg-red-500"></div>
        <span className="text-red-400 font-medium text-xs md:text-sm">
          Offline
        </span>
      </>
    )}
  </div>
);

export default function CameraDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSection = location.state?.activeSection || "cameras";

  const [camera, setCamera] = useState(null);
  const [loadingCamera, setLoadingCamera] = useState(true);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("live"); 
  // "live" | "review"


  const [date, setDate] = useState(
    new Date()
      .toLocaleDateString("en-CA", {
        timeZone: "America/Vancouver",
      })
      .slice(0, 10)
  );

  const [fullRecordings, setFullRecordings] = useState([]);
  const [detections, setDetections] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // concat playback data
  const [concatFileUrl, setConcatFileUrl] = useState(null);
  const [concatSegments, setConcatSegments] = useState([]);

  // For multi-cam switcher
  const [cameraList, setCameraList] = useState([]);

  // ====== FETCH HELPERS ======

  const fetchCamera = async () => {
    try {
      setLoadingCamera(true);
      const res = await authAxios.get(`/cameras/${id}/`);
      setCamera(res.data);
      setError("");
    } catch (err) {
      console.error("Failed to fetch camera:", err);
      setError("Failed to load camera details");
    } finally {
      setLoadingCamera(false);
    }
  };

  const fetchSummary = async () => {
    if (!id) return;
    try {
      const res = await authAxios.get(`/analytics/${id}/summary/`, {
        params: { date },
      });
      setSummary(res.data);
    } catch (err) {
      console.warn("Failed to fetch summary:", err);
      setSummary(null);
    }
  };

  const fetchFullRecordings = async () => {
    if (!id) return;
    try {
      const res = await authAxios.get(`/recordings/full/${id}/`, {
        params: { date },
      });
      setFullRecordings(res.data.files || []);
    } catch (err) {
      console.error("❌ Failed to load full recordings for timeline", err);
      setFullRecordings([]);
    }
  };

  const fetchDetections = async () => {
    if (!id) return;
    setLoadingTimeline(true);
    try {
      const res = await authAxios.get(`/recordings/concat/${id}/`, {
        params: { date },
      });

      const detectionData = res.data?.detections || [];
      const fileUrl = res.data?.file_url || null;
      const segments = res.data?.segments || [];


// 🔑 REST fetch = authoritative snapshot
setDetections(
  detectionData.sort(
    (a, b) =>
      new Date(a.timestamp).getTime() -
      new Date(b.timestamp).getTime()
  )
);


      setConcatFileUrl(fileUrl);
      setConcatSegments(segments);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error("Failed to fetch detections/concat data:", err);
      }
      setConcatFileUrl(null);
      setConcatSegments([]);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const fetchCameraList = async () => {
    try {
      const res = await authAxios.get("/cameras/");
      const data = Array.isArray(res.data)
        ? res.data
        : res.data.results || [];
      setCameraList(data);
    } catch (err) {
      console.warn("Failed to fetch camera list (multi-cam switcher):", err);
      setCameraList([]);
    }
  };

  // ====== INITIAL LOAD ======
useEffect(() => {
  // 🔥 CLEAR timeline state on camera switch
  setDetections([]);
  setConcatFileUrl(null);
  setConcatSegments([]);
}, [id]);


  useEffect(() => {
    fetchCamera();
    fetchCameraList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!camera) return;
    fetchSummary();
    fetchFullRecordings();
    fetchDetections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera, date]);

  // ====== LIVE WS DETECTIONS (REAL-TIME) ======

  useEffect(() => {
    if (!id) return;

    const ws = new WebSocket(
      `${window.location.protocol === "https:" ? "wss" : "ws"}://${
        window.location.host
      }/ws/camera/${id}/analytics/`
    );


ws.onmessage = (event) => {
  try {
    const payload = JSON.parse(event.data);
    if (!payload.timestamp) return;

    const det = {
      id: payload.id,
      timestamp: payload.timestamp,
      time: payload.time,
      label: payload.label,
      event: payload.event,
      zone: payload.zone,
      confidence: payload.confidence,
      source: "ws",
    };

    setDetections((prev) => {
      if (prev.some((d) => d.id === det.id)) return prev;
      return [...prev, det];
    });
  } catch (e) {
    console.warn("WS parse failed", e);
  }
};


    ws.onclose = () => console.log("Analytics WS closed");

    return () => ws.close();
  }, [id, date]);

  // ====== META / COMPUTED ======

  const isOnline =
    (camera?.connection_status || "").toLowerCase() === "online";

  const siteTimezone =
    summary?.site_timezone || "America/Vancouver";

  // Peak hour (detections + full recordings)
// Only use detections for peak hour
const detectionTimes = detections
  .map((d) => d.timestamp || d.start_time)
  .filter(Boolean);

let peakHour = null;
if (detectionTimes.length > 0) {
  const counts = {};
  detectionTimes.forEach((ts) => {
    const localDate = new Date(ts);
    const hour = localDate.toLocaleString("en-CA", {
      hour: "2-digit",
      hour12: false,
      timeZone: siteTimezone,
    });
    counts[hour] = (counts[hour] || 0) + 1;
  });
  
  const maxHour = Object.keys(counts).reduce((a, b) =>
    counts[a] > counts[b] ? a : b
  );
  
  const hourNum = parseInt(maxHour, 10);
  peakHour = { 
    hour: maxHour,
    count: counts[maxHour],
    displayHour: hourNum,  // ✅ Store numeric hour
  };
}


// ✅ NEW: Comprehensive detection statistics
const detectionStats = useMemo(() => {
  if (!detections.length) return null;
  
  const times = detections
    .map(d => new Date(d.timestamp || d.start_time))
    .filter(t => !isNaN(t.getTime()))
    .sort((a, b) => a - b);
  
  if (!times.length) return null;
  
  const firstDetection = times[0];
  const lastDetection = times[times.length - 1];
  
  // Average per hour
  const hourSpan = (lastDetection - firstDetection) / (1000 * 60 * 60);
  const avgPerHour = hourSpan > 0 ? (detections.length / hourSpan).toFixed(1) : '0';
  
  // Busiest zone
  const zoneCounts = {};
  detections.forEach(d => {
    if (d.zone) {
      zoneCounts[d.zone] = (zoneCounts[d.zone] || 0) + 1;
    }
  });
  
  const busiestZone = Object.keys(zoneCounts).length > 0
    ? Object.keys(zoneCounts).reduce((a, b) => 
        zoneCounts[a] > zoneCounts[b] ? a : b
      )
    : null;
  
  return {
    firstDetection,
    lastDetection,
    avgPerHour,
    busiestZone,
    busiestZoneCount: busiestZone ? zoneCounts[busiestZone] : 0,
  };
}, [detections]);




  // Approx recording coverage (assuming 5-min segments)
  const segmentsPerDay = (24 * 60) / 5; // 5 minutes per segment
  const coveragePercent = Math.min(
    100,
    ((fullRecordings.length || 0) / segmentsPerDay) * 100
  );

  // Simple "health" score
  const healthStatus = (() => {
    if (isOnline && coveragePercent > 80) return { label: "Healthy", color: "text-emerald-400" };
    if (isOnline && coveragePercent > 40) return { label: "Degraded", color: "text-yellow-400" };
    if (!isOnline && coveragePercent > 10) return { label: "Offline (Buffered)", color: "text-orange-400" };
    return { label: "Critical", color: "text-red-400" };
  })();

  // ====== RENDER ======

  if (loadingCamera) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-lg">
            Loading camera...
          </p>
        </div>
      </div>
    );
  }

  if (error || !camera) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center px-4">
          <FiAlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <h2 className="text-white text-2xl font-bold mb-2">
            {error || "Camera not found"}
          </h2>
          <button
            onClick={() =>
              navigate("/dashboard", {
                state: { activeSection: fromSection },
              })
            }
            className="mt-4 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* HEADER */}
      <header className="sticky top-0 z-30 bg-black/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 lg:gap-4">
            {/* Left: title, status */}
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={() =>
                  navigate("/dashboard", {
                    state: { activeSection: fromSection },
                  })
                }
                className="p-2 hover:bg-gray-900 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <FiChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <div className="space-y-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm sm:text-sm font-bold">
                    {camera.name}
                  </p>
                  <StatusIndicator online={isOnline} />
                </div>
              </div>
            </div>

            {/* Right: date & meta */}
            <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm">
              <div className="hidden md:flex items-center gap-2 text-gray-400">
                <FiCamera className="w-4 h-4" />
                <span className="font-mono text-xs">
                  {camera.ip_address}
                  {camera.port ? `:${camera.port}` : ""}
                </span>
              </div>

              <div className="flex items-center gap-2 bg-gray-900/60 rounded px-3 py-1.5 border border-gray-800">
                <FiClock className="w-3 h-3 text-gray-400" />
                <input
                  type="date"
                  value={date}
                  max={new Date()
                    .toLocaleDateString("en-CA", {
                      timeZone: "America/Vancouver",
                    })
                    .slice(0, 10)}
                  onChange={(e) => setDate(e.target.value)}
                  className="bg-transparent text-white text-xs focus:outline-none"
                />
              </div>

              <div className="flex items-center gap-2 bg-gray-900/60 rounded px-3 py-1.5 border border-gray-800">
                <FiActivity className="w-3 h-3 text-gray-400" />
<span className="text-xs">
  Peak:{" "}
  {peakHour ? (
    <span className="font-semibold text-emerald-400">
      {peakHour.displayHour}:00 ({peakHour.count})
    </span>
  ) : (
    <span className="text-gray-500">No data</span>
  )}
</span>


              </div>

              <div className="hidden sm:flex items-center gap-1.5 bg-gray-900/60 rounded px-3 py-1.5 border border-gray-800">
                <FiServer className="w-3 h-3 text-gray-400" />
                <span
                  className={`text-xs font-semibold ${healthStatus.color}`}
                >
                  {healthStatus.label}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* BODY */}
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* DVR Player + Timeline + Motion Feed + Bookmarks */}
        <EnterpriseDVRPlayer
          cameraId={camera.id}
          camera={camera}
          date={date}
          timezone={siteTimezone}
          fullRecordings={fullRecordings}
          detections={detections}
          loadingTimeline={loadingTimeline}
          concatFileUrl={concatFileUrl}
          concatSegments={concatSegments}
          cameraList={cameraList}
	  mode={mode}
	  setMode={setMode}
          onSwitchCamera={(targetId) =>
            navigate(`/camera/${targetId}`)
          }
	  onRefreshDetections={fetchDetections}
        />

        {/* STATUS & QUICK STATS CARD (client-facing friendly) */}
        <div className="bg-gray-950 rounded-xl border border-gray-800 p-4 sm:p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg md:text-xl font-bold flex items-center gap-2">
              <FiCamera className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
              Camera Overview
            </h2>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400">
              <span className="hidden sm:inline">
                Timezone:
              </span>
              <span className="font-mono">
                {siteTimezone}
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Left: identity */}

            {/* Middle: quick stats */}
{/* Enhanced Camera Overview with 8 metrics */}
<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
  {/* Total Detections */}
  <div className="bg-gray-900/60 rounded-lg p-3">
    <div className="text-xs text-gray-400 mb-1">Total Detections</div>
    <div className="text-2xl font-bold text-red-400">
      {detections.length}
    </div>
  </div>
  
  {/* Peak Hour */}
  <div className="bg-gray-900/60 rounded-lg p-3">
    <div className="text-xs text-gray-400 mb-1">Peak Hour</div>
    {peakHour ? (
      <>
        <div className="text-2xl font-bold text-emerald-400">
          {peakHour.displayHour}:00
        </div>
        <div className="text-[10px] text-gray-500 mt-0.5">
          {peakHour.count} detections
        </div>
      </>
    ) : (
      <div className="text-gray-600 text-sm">—</div>
    )}
  </div>
  
  {/* First Detection */}
  <div className="bg-gray-900/60 rounded-lg p-3">
    <div className="text-xs text-gray-400 mb-1">First Detection</div>
    {detectionStats?.firstDetection ? (
      <>
        <div className="text-lg font-bold text-blue-400">
          {detectionStats.firstDetection.toLocaleTimeString('en-CA', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
        <div className="text-[10px] text-gray-500 mt-0.5">
          {detectionStats.firstDetection.toLocaleDateString('en-CA')}
        </div>
      </>
    ) : (
      <div className="text-gray-600 text-sm">—</div>
    )}
  </div>
  
  {/* Last Detection */}
  <div className="bg-gray-900/60 rounded-lg p-3">
    <div className="text-xs text-gray-400 mb-1">Last Detection</div>
    {detectionStats?.lastDetection ? (
      <>
        <div className="text-lg font-bold text-purple-400">
          {detectionStats.lastDetection.toLocaleTimeString('en-CA', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
        <div className="text-[10px] text-gray-500 mt-0.5">
          {detectionStats.lastDetection.toLocaleDateString('en-CA')}
        </div>
      </>
    ) : (
      <div className="text-gray-600 text-sm">—</div>
    )}
  </div>
  
  {/* Average Per Hour */}
  <div className="bg-gray-900/60 rounded-lg p-3">
    <div className="text-xs text-gray-400 mb-1">Avg Per Hour</div>
    <div className="text-2xl font-bold text-yellow-400">
      {detectionStats?.avgPerHour || '0'}
    </div>
  </div>
  
  {/* Busiest Zone */}
  <div className="bg-gray-900/60 rounded-lg p-3">
    <div className="text-xs text-gray-400 mb-1">Busiest Zone</div>
    {detectionStats?.busiestZone ? (
      <>
        <div className="text-lg font-bold text-orange-400 truncate">
          {detectionStats.busiestZone}
        </div>
        <div className="text-[10px] text-gray-500 mt-0.5">
          {detectionStats.busiestZoneCount} detections
        </div>
      </>
    ) : (
      <div className="text-gray-600 text-sm">—</div>
    )}
  </div>
  
  {/* Full Clips */}
  <div className="bg-gray-900/60 rounded-lg p-3">
    <div className="text-xs text-gray-400 mb-1">Full Clips</div>
    <div className="text-2xl font-bold text-blue-400">
      {fullRecordings.length}
    </div>
  </div>
  
  {/* Coverage */}
  <div className="bg-gray-900/60 rounded-lg p-3">
    <div className="text-xs text-gray-400 mb-1">Coverage</div>
    <div className="text-2xl font-bold text-emerald-400">
      {coveragePercent.toFixed(0)}%
    </div>
    <div className="text-[10px] text-gray-500 mt-0.5">
      Day coverage
    </div>
  </div>
</div>





            {/* Right: coverage & health */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-gray-400">
                Recording Coverage
              </h3>
              <div>
                <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                  <span>Day coverage</span>
                  <span className="font-semibold text-gray-200">
                    {coveragePercent.toFixed(0)}%
                  </span>
                </div>
                <div className="h-2 w-full bg-gray-900 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-400 via-yellow-400 to-red-400"
                    style={{ width: `${coveragePercent}%` }}
                  />
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  Estimated from 5-minute segments. More segments =
                  higher resilience to outages.
                </p>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-gray-400">
                  Health:
                </span>
                <span
                  className={`font-semibold ${healthStatus.color}`}
                >
                  {healthStatus.label}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Scrollbar Styles */}
        <style>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(31, 41, 55, 0.5);
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(75, 85, 99, 0.8);
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(107, 114, 128, 1);
          }
        `}</style>
      </div>
    </div>
  );
}

